/* The cart furniture (site-furniture spec §5.3), as custom elements, so the theme's redraws of the drawer and the
   cart page bring it back by themselves:
   - <gr-cart-meter> puts the delivery meter into words and fills its spectrum bar, from the rules Liquid rendered;
   - <gr-gift-note> saves the gift switch and note as cart attributes as they change;
   - <gr-cart-pair> adds the suggested pair, then asks the theme to redraw the cart;
   - <gr-cart-extras> opens the drawer's gift, discount code and note panels, one at a time, and ticks each once set
     (cart drawer room spec §4).
   Loaded by both the drawer and the cart page, so it defines its elements only once. Each element's words come from
   the data-gr-i18n block its snippet renders inside it, with English as the fallback; <gr-cart-extras> writes no words
   of its own beyond the applied codes. */
(() => {
  if (window.customElements.get('gr-cart-meter')) return;
  // Liquid's t filter HTML-escapes what it outputs ("Couldn&#39;t"): back to plain text for textContent
  const ENTITIES = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'", '&#x27;': "'" };
  const decode = (s) => (typeof s === 'string' ? s.replace(/&(?:amp|lt|gt|quot|#39|#x27);/g, (m) => ENTITIES[m]) : s);
  const words = (el, name, defaults) => {
    const out = Object.assign({}, defaults);
    let got = null;
    try { const block = el.querySelector(`[data-gr-i18n="${name}"]`); got = block && JSON.parse(block.textContent); } catch (e) { got = null; }
    Object.keys(got || {}).forEach((k) => { if (typeof got[k] === 'string') out[k] = decode(got[k]); });
    return out;
  };
  const fmt = (str, vars) => str.replace(/\{(\w+)\}/g, (m, k) => (Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : m));
  const METER = {
    checkout: 'Delivery {to_place} is worked out at checkout.',
    uk_made_charge: 'UK-made sizes are sent from our UK lab and add {amount}.',
    large_charge: '36×48″ sizes add {amount}.',
    more_removes: '{amount} more removes the {charge} standard delivery.',
    paid: 'Delivery {to_place}: {amount}',
    away: '{amount} away from free delivery {to_place}',
    free_uk: 'Free UK delivery',
    free_to: 'Free delivery {to_place}',
  };
  const GIFT = { gift_saving: 'Saving…', gift_saved: 'Saved', gift_error: "Couldn't save the note. Try again." };
  const PAIR = { pair_adding: 'Adding…', added: 'Added ✓' };
  const cartUrl = (path) => (window.Shopify?.routes?.root || '/') + path;   // /fr/cart/add.js in a French storefront
  const currency = () => window.Shopify?.currency?.active || 'GBP';
  const rate = () => Number(window.Shopify?.currency?.rate) || 1;
  const NUM_LOCALE = (() => {                     // the page's language; English keeps the en-GB amounts it always had
    const lang = document.documentElement.lang || '';
    try { return !/^en\b/i.test(lang) && Intl.NumberFormat.supportedLocalesOf(lang).length ? lang : 'en-GB'; } catch (e) { return 'en-GB'; }
  })();
  const money = (cents) => new Intl.NumberFormat(NUM_LOCALE, { style: 'currency', currency: currency(), currencyDisplay: 'narrowSymbol' })
    .format(cents / 100);                                // "$67.05", as the page's own prices read
  // A £ amount (in pence) in the shopper's currency, converted as Shopify converts it. A threshold is rounded up to a
  // whole unit, so the meter never promises free delivery that checkout won't give.
  const local = (pence, threshold = false) => {
    const x = pence * rate();
    return threshold && currency() !== 'GBP' ? Math.ceil(x / 100) * 100 : Math.round(x);
  };

  /* { text, notes, fill } for the meter's data, in the words T gives. Each delivery profile in the cart adds its own
     charge, and a free-from threshold counts the whole cart: Shopify's own sums (delivery_rules.py verify).
     place is the country's whole "to …" phrase. */
  function meter({ total, country, place, profiles, rules }, T = METER) {
    const to_place = decode(place);
    const parsed = {};
    for (const p of profiles) {
      const parts = (rules[p] || '').split('|').map(Number);
      if (parts.length !== 4) return { text: fmt(T.checkout, { to_place }), notes: [], fill: null };
      parsed[p] = parts;
    }
    let away = 0, threshold = 0, fixed = 0, unmet = 0;
    const notes = [];
    for (const p of profiles) {
      const [free, charge] = parsed[p];
      if (free > 0) {
        const t = local(free, true);
        if (total < t) {
          unmet += local(charge);
          if (t - total > away) { away = t - total; threshold = t; }
        }
      } else if (free < 0) {
        fixed += local(charge);
        notes.push(fmt(p === 'uk-made' ? T.uk_made_charge : T.large_charge, { amount: money(local(charge)) }));
      }
    }
    if (fixed > 0) {                                    // some sizes always cost: never promise free delivery
      if (away > 0) notes.push(fmt(T.more_removes, { amount: money(away), charge: money(unmet) }));
      return { text: fmt(T.paid, { to_place, amount: money(fixed + unmet) }), notes, fill: null };
    }
    if (away > 0) return { text: fmt(T.away, { amount: money(away), to_place }), notes, fill: Math.min(1, total / threshold) };
    return { text: country === 'GB' ? T.free_uk : fmt(T.free_to, { to_place }), notes, fill: 1 };
  }

  class Meter extends HTMLElement {
    connectedCallback() {
      this.draw();
      this.watch = this.watch || new MutationObserver(() => this.draw());
      this.watch.observe(this, { childList: true });          // the theme swaps the contents on a quantity change
    }

    disconnectedCallback() {
      this.watch?.disconnect();
    }

    draw() {
      const json = this.querySelector('script[type="application/json"]:not([data-gr-i18n])');
      if (!json) return;
      let data, state;
      try { data = JSON.parse(json.textContent); state = meter(data, words(this, 'cart-meter', METER)); } catch (e) { return; }
      document.dispatchEvent(new CustomEvent('gr:cart-count', { detail: data.count }));   // the pair isn't redrawn with it
      const text = this.querySelector('.gr-meter__text'), note = this.querySelector('.gr-meter__note'), bar = this.querySelector('.gr-meter__bar');
      if (text && text.textContent !== state.text) text.textContent = state.text;
      if (note) {
        const words = state.notes.join(' ');
        if (note.textContent !== words) note.textContent = words;
        note.hidden = !words;
      }
      if (bar) {
        bar.hidden = state.fill === null;
        bar.style.setProperty('--fill', String(state.fill ?? 0));
      }
    }
  }

  const post = (url, body) => fetch(url, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body),
  }).then((r) => { if (!r.ok) throw new Error(`${url}: ${r.status}`); return r.json(); });

  class Gift extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const on = this.querySelector('[data-gr-gift-on]'), note = this.querySelector('[data-gr-gift-note]');
      const body = this.querySelector('[data-gr-gift-body]'), status = this.querySelector('[data-gr-gift-status]');
      if (!on || !note) return;
      const T = words(this, 'gift-note', GIFT);
      let timer;
      const save = () => {
        clearTimeout(timer);
        const attributes = on.checked ? { Gift: 'yes', 'Gift note': note.value.slice(0, 200) } : { Gift: '', 'Gift note': '' };
        status.textContent = on.checked ? T.gift_saving : '';
        post(cartUrl('cart/update.js'), { attributes })
          .then(() => { status.textContent = on.checked ? T.gift_saved : ''; })
          .catch(() => { status.textContent = T.gift_error; });
      };
      on.addEventListener('change', () => {
        body.hidden = !on.checked;
        if (!on.checked) note.value = '';
        save();
        if (on.checked) note.focus();
      });
      note.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(save, 700); });
      note.addEventListener('change', save);
    }
  }

  class Pair extends HTMLElement {
    connectedCallback() {
      this.onCount = this.onCount || ((e) => {                // the discount is once per order
        const save = this.querySelector('.gr-pair__save');
        if (save) save.hidden = e.detail !== 1;
      });
      document.addEventListener('gr:cart-count', this.onCount);
      if (this.ready) return;
      this.ready = true;
      const add = this.querySelector('[data-gr-pair-add]'), error = this.querySelector('[data-gr-pair-error]');
      const T = words(this, 'cart-pair', PAIR);
      add?.addEventListener('click', async () => {
        const label = add.textContent;
        add.disabled = true;
        add.textContent = T.pair_adding;
        if (error) error.hidden = true;
        try {
          await post(cartUrl('cart/add.js'), { items: [{ id: Number(this.dataset.variant), quantity: 1 }] });
          add.textContent = T.added;
          if (window.SHTHelper?.forceUpdateCartStatus) window.SHTHelper.forceUpdateCartStatus();
          else window.location.reload();
        } catch (e) {
          add.disabled = false;
          add.textContent = label;
          if (error) error.hidden = false;
        }
      });
    }
  }

  /* The panels arrive open, so they still work if this script never runs; it closes them as it starts. The theme
     redraws only the applied codes after a code is applied or removed, and the whole drawer after an add or a
     removal, which brings a fresh element. */
  class Extras extends HTMLElement {
    connectedCallback() {
      if (!this.ready) this.setUp();
      if (this.pills) this.watch.observe(this.pills, { childList: true, subtree: true });
    }

    disconnectedCallback() {
      this.watch?.disconnect();
    }

    setUp() {
      this.ready = true;
      this.show(null);
      const details = this.querySelector('[data-gr-panel="code"] details');
      if (details) details.open = true;                       // the theme's discount box, already open in its panel
      this.classList.add('is-ready');
      this.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-gr-chip]');
        if (!chip) return;
        const name = chip.dataset.grChip, open = chip.getAttribute('aria-expanded') === 'true';
        this.show(open ? null : name);
        if (!open) this.panel(name)?.querySelector('input:not([type="hidden"]), textarea, select, button')?.focus();
      });
      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-gr-gift-on]')) this.tick('gift', e.target.checked);
      });
      this.addEventListener('input', (e) => {
        if (e.target.name === 'note') this.tick('note', !!e.target.value.trim());
      });
      this.pills = this.querySelector('.js-discount-pills');
      this.watch = new MutationObserver(() => this.codes());
    }

    panel(name) {
      return this.querySelector(`[data-gr-panel="${name}"]`);
    }

    show(name) {                                              // null closes them all
      for (const chip of this.querySelectorAll('[data-gr-chip]')) {
        const open = chip.dataset.grChip === name;
        chip.setAttribute('aria-expanded', String(open));
        const panel = this.panel(chip.dataset.grChip);
        if (panel) panel.hidden = !open;
      }
    }

    tick(name, on) {
      this.querySelector(`[data-gr-chip="${name}"]`)?.classList.toggle('is-set', on);
    }

    codes() {                                                 // "LUVCOLOR10", "LUVCOLOR10 +1": no words to translate
      const found = [...this.pills.querySelectorAll('[data-discount-code]')].map((el) => el.dataset.discountCode).filter(Boolean);
      const label = this.querySelector('[data-gr-chip="code"] [data-gr-chip-label]');
      if (label) label.textContent = found.length ? found[0] + (found.length > 1 ? ` +${found.length - 1}` : '') : label.dataset.grEmpty;
      this.tick('code', found.length > 0);
    }
  }

  Pair.prototype.disconnectedCallback = function () { document.removeEventListener('gr:cart-count', this.onCount); };
  window.customElements.define('gr-cart-meter', Meter);
  window.customElements.define('gr-gift-note', Gift);
  window.customElements.define('gr-cart-pair', Pair);
  window.customElements.define('gr-cart-extras', Extras);
})();
